(() => {
  const GATE_SELECTOR = '[data-drop-release-gate]';
  const LOCKED_SELECTOR = '[data-drop-locked]';
  const LIVE_SELECTOR = '[data-drop-live]';

  const MAX_TIMEOUT = 2147483647;

  const initializedGates = new WeakSet();
  const pendingGates = new Set();

  function getReleaseTime(gate) {
    const epoch = Number(gate.dataset.releaseEpoch);

    if (!Number.isFinite(epoch) || epoch <= 0) {
      return null;
    }

    return epoch * 1000;
  }

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function isProductPageGate(gate) {
    return !!gate.querySelector('[data-drop-countdown]');
  }

  function findProductInfoScope(gate) {
    return (
      gate.closest('.product__info-container') ||
      gate.closest('.product__info-wrapper') ||
      gate.closest('.product__info') ||
      gate.parentElement
    );
  }

  function findPriceElement(scope) {
    if (!scope) return null;

    return (
      scope.querySelector('.price') ||
      scope.querySelector('.price__container')
    );
  }

  function ensurePriceBadge(gate) {
    if (!isProductPageGate(gate)) {
      return;
    }

    const scope = findProductInfoScope(gate);
    const priceElement = findPriceElement(scope);

    if (!scope || !priceElement) {
      return;
    }

    let row = scope.querySelector('[data-drop-price-row]');

    if (!row) {
      row = document.createElement('div');
      row.className = 'drop-release-price-row';
      row.setAttribute('data-drop-price-row', '');

      priceElement.parentNode.insertBefore(row, priceElement);
      row.appendChild(priceElement);
    }

    let badge = row.querySelector('[data-drop-price-badge]');

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'drop-release-price-badge';
      badge.setAttribute('data-drop-price-badge', '');
      badge.textContent = 'COMING SOON';
      row.appendChild(badge);
    }

    badge.hidden = false;
  }

  function hidePriceBadge(gate) {
    const scope = findProductInfoScope(gate);

    if (!scope) return;

    const badge = scope.querySelector('[data-drop-price-badge]');

    if (badge) {
      badge.hidden = true;
    }
  }

  function updateCountdown(gate, remaining) {
    const countdown = gate.querySelector('[data-drop-countdown]');

    if (!countdown) {
      return;
    }

    const totalSeconds = Math.max(0, Math.floor(remaining / 1000));

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const daysElement = countdown.querySelector('[data-drop-days]');
    const hoursElement = countdown.querySelector('[data-drop-hours]');
    const minutesElement = countdown.querySelector('[data-drop-minutes]');
    const secondsElement = countdown.querySelector('[data-drop-seconds]');

    if (daysElement) daysElement.textContent = pad(days);
    if (hoursElement) hoursElement.textContent = pad(hours);
    if (minutesElement) minutesElement.textContent = pad(minutes);
    if (secondsElement) secondsElement.textContent = pad(seconds);
  }

  function clearReleaseTimer(gate) {
    if (gate._dropReleaseTimer) {
      window.clearTimeout(gate._dropReleaseTimer);
      gate._dropReleaseTimer = null;
    }
  }

  function clearCountdownTimer(gate) {
    if (gate._dropCountdownTimer) {
      window.clearInterval(gate._dropCountdownTimer);
      gate._dropCountdownTimer = null;
    }
  }

  function clearTimers(gate) {
    clearReleaseTimer(gate);
    clearCountdownTimer(gate);
  }

  function showLocked(gate) {
    const locked = gate.querySelector(LOCKED_SELECTOR);
    const live = gate.querySelector(LIVE_SELECTOR);

    if (!locked || !live) {
      return;
    }

    locked.hidden = false;
    live.hidden = true;

    ensurePriceBadge(gate);

    gate.dataset.dropReleaseState = 'locked';
  }

  function showLive(gate) {
    const locked = gate.querySelector(LOCKED_SELECTOR);
    const live = gate.querySelector(LIVE_SELECTOR);

    if (!locked || !live) {
      return;
    }

    locked.hidden = true;
    live.hidden = false;

    hidePriceBadge(gate);

    gate.dataset.dropReleaseState = 'live';
  }

  function startCountdown(gate, releaseTime) {
    const countdown = gate.querySelector('[data-drop-countdown]');

    if (!countdown) {
      return;
    }

    clearCountdownTimer(gate);

    const tick = () => {
      const remaining = releaseTime - Date.now();

      if (remaining <= 0) {
        clearCountdownTimer(gate);
        syncGate(gate);
        return;
      }

      updateCountdown(gate, remaining);
    };

    tick();

    gate._dropCountdownTimer = window.setInterval(tick, 1000);
  }

  function syncGate(gate) {
    if (!document.documentElement.contains(gate)) {
      clearTimers(gate);
      pendingGates.delete(gate);
      return;
    }

    const releaseTime = getReleaseTime(gate);

    if (releaseTime === null) {
      clearTimers(gate);
      pendingGates.delete(gate);
      return;
    }

    const remaining = releaseTime - Date.now();

    if (remaining <= 0) {
      clearTimers(gate);
      updateCountdown(gate, 0);
      showLive(gate);
      pendingGates.delete(gate);
      return;
    }

    showLocked(gate);

    pendingGates.add(gate);

    updateCountdown(gate, remaining);

    clearReleaseTimer(gate);

    gate._dropReleaseTimer = window.setTimeout(() => {
      syncGate(gate);
    }, Math.min(remaining + 25, MAX_TIMEOUT));

    startCountdown(gate, releaseTime);
  }

  function initializeGate(gate) {
    if (!(gate instanceof Element)) {
      return;
    }

    if (initializedGates.has(gate)) {
      return;
    }

    initializedGates.add(gate);
    syncGate(gate);
  }

  function scan(root = document) {
    if (root instanceof Element && root.matches(GATE_SELECTOR)) {
      initializeGate(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(GATE_SELECTOR).forEach(initializeGate);
    }
  }

  function resyncPendingGates() {
    Array.from(pendingGates).forEach(syncGate);
  }

  function start() {
    scan(document);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            scan(node);
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        resyncPendingGates();
      }
    });

    window.addEventListener('pageshow', () => {
      resyncPendingGates();
      scan(document);
    });

    document.addEventListener('shopify:section:load', (event) => {
      scan(event.target);
      resyncPendingGates();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }
})();